// ANGEL FLIGHT — mission grading: letter grade + earned medals (PURE).
//
// The after-action report's brain. From the RunStats the sim already tracks
// (kills, headshots, bestCombo, friendliesHit, elapsedMs) plus the cleared flag,
// this derives ONE score, maps it to a letter grade (S/A/B/C/D), and resolves a
// fixed roster of medals via obvious boolean predicates. No DOM, no Three.js, no
// randomness — same input, same output — so the whole thing is unit-testable
// without booting the renderer. This is the SINGLE SOURCE of the mission score
// formula: GameLoop banks it, the Debrief renders it, nothing re-derives it.

import type { MissionResult, RunStats } from '../core/types';

export type Grade = 'S' | 'A' | 'B' | 'C' | 'D';

/** Score weights + thresholds + medal cutoffs. One place to tune the report.
 *
 *  THE FORMULA (scoreMission is the single implementation):
 *
 *    score = kills × perKill
 *          + headshots × perHeadshot
 *          + bestCombo × perCombo
 *          + accuracyMax × (shotsHit / shotsFired)        [0 if no shots]
 *          − friendly strikes × friendlyPenalty
 *          + (cleared ? clearBonus + timeBonus(≤ time.maxBonus) : 0)
 *    floored at 0; a FAILED run is capped at failScoreCap.
 *
 *  Design intent (the G1 rebalance): the grade must reward GUNNING — kills,
 *  accuracy, and streaks dominate, while the clear+time bonus is small enough
 *  that a hide-and-wait clear (fire into the sky, survive the timer) cannot
 *  out-grade an aggressive run. Reference points at these weights: a passive
 *  ~12-kill survive-clear lands ~C; a committed ~30-kill clear lands ~A; S
 *  demands a near-full clear with streaks + accuracy. */
export const GRADING = Object.freeze({
  /** Per-stat score weights (additive). Kills/accuracy/streaks reward skill,
   *  friendly strikes are a HEAVY flat penalty (and the score floors at zero). */
  weights: {
    perKill: 40,
    perHeadshot: 50,
    perCombo: 60,
    /** Max accuracy bonus, scaled linearly by shotsHit / shotsFired. */
    accuracyMax: 300,
    /** Flat bonus for completing the objective (a cleared run outscores a wipe
     *  at equal kills) — deliberately SMALL so clearing alone never carries a
     *  grade. */
    clearBonus: 250,
    /** Subtracted per friendly-fire STRIKE — discipline matters more than volume. */
    friendlyPenalty: 400,
  },
  /** Speed bonus for a CLEARED run: faster clears score higher. Linear from the
   *  full bonus at `fastMs` down to zero at `slowMs`; never negative. Ignored on
   *  a failed run (you don't get a time bonus for dying quickly). Note survive
   *  missions end at a fixed elapsed time, so for them this is effectively a
   *  small per-mission constant — another reason it stays small. */
  time: {
    fastMs: 60_000,
    slowMs: 240_000,
    maxBonus: 150,
  },
  /** A FAILED mission can never grade above this ceiling, regardless of score —
   *  a wipe is a wipe. Maps into the C/D band below. */
  failScoreCap: 900,
  /** score ≥ threshold ⇒ grade. Evaluated top-down (S first). D is the floor. */
  thresholds: [
    { grade: 'S' as Grade, min: 3200 },
    { grade: 'A' as Grade, min: 2200 },
    { grade: 'B' as Grade, min: 1400 },
    { grade: 'C' as Grade, min: 700 },
    { grade: 'D' as Grade, min: 0 },
  ],
  /** Medal predicate cutoffs (kept obvious + named). */
  medals: {
    sharpshooterHeadshots: 10,
    marksmanHeadshots: 25,
    executionerKills: 40,
    angelOfDeathKills: 80,
    comboKing: 12,
    comboLegend: 20,
    centurionKills: 100,
  },
} as const);

/* ──────────────────────────────────────────────────────────────────────────
 * Score
 * ──────────────────────────────────────────────────────────────────────── */

/** Linear time bonus for a cleared run (0 on a failed run). Floored at zero. */
function timeBonus(stats: RunStats, cleared: boolean): number {
  if (!cleared) return 0;
  const { fastMs, slowMs, maxBonus } = GRADING.time;
  if (stats.elapsedMs <= fastMs) return maxBonus;
  if (stats.elapsedMs >= slowMs) return 0;
  const t = (slowMs - stats.elapsedMs) / (slowMs - fastMs); // 1 → 0
  return Math.round(maxBonus * t);
}

/** Accuracy bonus: accuracyMax scaled by the hostile hit-rate. Zero when no
 *  rounds were fired (a no-shot run earns no accuracy credit). */
function accuracyBonus(stats: RunStats): number {
  if (stats.shotsFired <= 0) return 0;
  const rate = Math.min(1, Math.max(0, stats.shotsHit / stats.shotsFired));
  return Math.round(GRADING.weights.accuracyMax * rate);
}

/**
 * The mission score — the SINGLE source of the formula (documented on GRADING
 * above). Kills/headshots/streaks/accuracy reward GUNNING; a small clear+time
 * bonus rewards finishing; each friendly-fire strike is a heavy flat dock.
 * Floored at zero; a failed run is capped at `failScoreCap` so a wipe can never
 * out-grade a clear.
 */
export function scoreMission(result: MissionResult): number {
  const s = result.stats;
  const w = GRADING.weights;
  let score =
    s.kills * w.perKill +
    s.headshots * w.perHeadshot +
    s.bestCombo * w.perCombo +
    accuracyBonus(s) -
    s.friendliesHit * w.friendlyPenalty;
  if (result.cleared) score += w.clearBonus + timeBonus(s, true);
  score = Math.max(0, Math.round(score));
  // A wipe is a wipe — capped. EXCEPT endless (Last Stand): the bird going down
  // is the natural end of that mode, not a failure, so its kill/streak score
  // stands uncapped (it's the whole point of the score chase).
  if (!result.cleared && result.objective !== 'endless') {
    score = Math.min(score, GRADING.failScoreCap);
  }
  return score;
}

/** Map a raw score to its letter grade (top-down thresholds; D is the floor). */
export function gradeForScore(score: number): Grade {
  for (const t of GRADING.thresholds) {
    if (score >= t.min) return t.grade;
  }
  return 'D';
}

/** Grade a resolved mission: its score + the letter it earns. */
export function gradeMission(result: MissionResult): { grade: Grade; score: number } {
  const score = scoreMission(result);
  return { grade: gradeForScore(score), score };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Medals
 * ──────────────────────────────────────────────────────────────────────── */

export type MedalId =
  | 'SHARPSHOOTER'
  | 'MARKSMAN'
  | 'EXECUTIONER'
  | 'ANGEL_OF_DEATH'
  | 'CENTURION'
  | 'COMBO_KING'
  | 'COMBO_LEGEND'
  | 'CLEAN_HANDS'
  | 'FLAWLESS'
  | 'AREA_SECURED'
  | 'BLITZ'
  | 'TOP_GRADE'
  | 'NEW_BEST';

export interface Medal {
  id: MedalId;
  /** Short ALL-CAPS chip label. */
  label: string;
  /** One-line description of how it was earned. */
  desc: string;
  earned: boolean;
}

/** Definition of a medal: its display copy + a pure predicate over the run. */
interface MedalDef {
  id: MedalId;
  label: string;
  desc: string;
  earned: (result: MissionResult, score: number, prevBest: number) => boolean;
}

const M = GRADING.medals;

/** The full medal roster. Order = display order (combat feats → discipline →
 *  meta). Every predicate is pure + obvious; `prevBest` defaults to 0. */
const MEDAL_DEFS: readonly MedalDef[] = [
  {
    id: 'SHARPSHOOTER',
    label: 'SHARPSHOOTER',
    desc: `${M.sharpshooterHeadshots}+ headshots`,
    earned: (r) => r.stats.headshots >= M.sharpshooterHeadshots,
  },
  {
    id: 'MARKSMAN',
    label: 'MARKSMAN',
    desc: `${M.marksmanHeadshots}+ headshots`,
    earned: (r) => r.stats.headshots >= M.marksmanHeadshots,
  },
  {
    id: 'EXECUTIONER',
    label: 'EXECUTIONER',
    desc: `${M.executionerKills}+ kills`,
    earned: (r) => r.stats.kills >= M.executionerKills,
  },
  {
    id: 'ANGEL_OF_DEATH',
    label: 'ANGEL OF DEATH',
    desc: `${M.angelOfDeathKills}+ kills`,
    earned: (r) => r.stats.kills >= M.angelOfDeathKills,
  },
  {
    id: 'CENTURION',
    label: 'CENTURION',
    desc: `${M.centurionKills}+ kills`,
    earned: (r) => r.stats.kills >= M.centurionKills,
  },
  {
    id: 'COMBO_KING',
    label: 'COMBO KING',
    desc: `x${M.comboKing} kill streak`,
    earned: (r) => r.stats.bestCombo >= M.comboKing,
  },
  {
    id: 'COMBO_LEGEND',
    label: 'COMBO LEGEND',
    desc: `x${M.comboLegend} kill streak`,
    earned: (r) => r.stats.bestCombo >= M.comboLegend,
  },
  {
    id: 'CLEAN_HANDS',
    label: 'CLEAN HANDS',
    desc: 'Cleared with zero friendly fire',
    earned: (r) => r.cleared && r.stats.friendliesHit === 0,
  },
  {
    id: 'FLAWLESS',
    label: 'FLAWLESS',
    desc: 'Cleared, no friendly fire, heavy kills',
    earned: (r) =>
      r.cleared &&
      r.stats.friendliesHit === 0 &&
      r.stats.kills >= M.executionerKills,
  },
  {
    id: 'AREA_SECURED',
    label: 'AREA SECURED',
    desc: 'Objective complete',
    earned: (r) => r.cleared,
  },
  {
    id: 'BLITZ',
    label: 'BLITZ',
    desc: `Cleared under ${Math.round(GRADING.time.fastMs / 1000)}s`,
    earned: (r) => r.cleared && r.stats.elapsedMs <= GRADING.time.fastMs,
  },
  {
    id: 'TOP_GRADE',
    label: 'TOP GRADE',
    desc: 'Earned an S rank',
    earned: (_r, score) => gradeForScore(score) === 'S',
  },
  {
    id: 'NEW_BEST',
    label: 'NEW BEST',
    desc: 'Beat your previous high score',
    // CLEARED runs (or an ENDLESS run, which banks its best on the downed run)
    // can claim NEW BEST — a plain survive/boss failure never banks, so it can't.
    // Honours the result's banked flag (GameLoop sets it against the best read
    // BEFORE banking); the score > prevBest fallback covers callers that pass the
    // persisted prevBest explicitly.
    earned: (r, score, prevBest) =>
      (r.cleared || r.objective === 'endless') && (r.newBest || score > prevBest),
  },
];

/**
 * Resolve every medal for a run. `prevBest` (the persisted bestScores entry,
 * default 0) drives only NEW BEST. Returns the full roster with `earned` set —
 * the Debrief decides whether to dim or hide the unearned ones.
 */
export function evaluateMedals(result: MissionResult, prevBest = 0): Medal[] {
  const score = scoreMission(result);
  return MEDAL_DEFS.map((def) => ({
    id: def.id,
    label: def.label,
    desc: def.desc,
    earned: def.earned(result, score, prevBest),
  }));
}

/** Just the earned medals, in display order. */
export function earnedMedals(result: MissionResult, prevBest = 0): Medal[] {
  return evaluateMedals(result, prevBest).filter((m) => m.earned);
}
